import { Component, OnInit, Inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DeviceInventoryService } from 'src/app/services/device-inventory/device-inventory.service';
import { Dropdown } from 'src/app/models/dropdown.model';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ColdStore } from 'src/app/models/device.model';

@Component({
  selector: 'app-customer-details-dialog',
  templateUrl: './customer-details-dialog.component.html',
  styleUrls: ['./customer-details-dialog.component.scss']
})
export class CustomerDetailsDialogComponent implements OnInit, OnDestroy {

  public customerDetailsForm: FormGroup;
  public customers: Dropdown[];
  public userDetails: any[];
  public profiles: Dropdown[];
  public profileTypes: Dropdown[];
  public userSites: any[];
  public commodities: Dropdown[];
  public environment: string = 'qa'; 
  public isLoading = false;
  public coordinates: { latitude: number; longitude: number } | null = null;
  public isGettingLocation = false;
  public locationError = '';
  public selectedEnvironment: string | null = null;
  public environmentSelectionMode = true;
  private destroy$ = new Subject<void>();

  constructor(
    private _fb: FormBuilder,
    private _devicesService: DeviceInventoryService,
    private _toastr: ToastrService,
    private _cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<CustomerDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { environment?: string; showEnvironmentSelection?: boolean, coldStoreData?:ColdStore }
  ) { }

  ngOnInit() {
    if (this.data && this.data.environment) {
      this.environment = this.data.environment;
      this.selectedEnvironment = this.data.environment;
      this.environmentSelectionMode = false;
    } else if (this.data && this.data.showEnvironmentSelection) {
      this.environmentSelectionMode = true;
    } else {
      this.environmentSelectionMode = false;
    }
    
    this._createForm();
    
    if (!this.environmentSelectionMode) {
      this.loadCustomerData();
    }
  }

  private _createForm() {
    this.customerDetailsForm = this._fb.group({
      customerId: ["", [Validators.required]],
      userId: [{value: "", disabled: true}, [Validators.required]],
      siteId: [{value: "", disabled: true}, [Validators.required]],
      profileId: [{value: "", disabled: true}, [Validators.required]],
      profileTypeId: [{value: "", disabled: true}, [Validators.required]],
      commodityId: [this.data.coldStoreData ? this.data.coldStoreData.commodity_id : "", [Validators.required]],
      name: [this.data.coldStoreData ? this.data.coldStoreData.name : "", [Validators.required]],
      latitude: [this.data.coldStoreData && this.data.coldStoreData.coordinates ? this.data.coldStoreData.coordinates.split(',')[0] : "", [Validators.required, Validators.min(-90), Validators.max(90)]],
      longitude: [this.data.coldStoreData && this.data.coldStoreData.coordinates ? this.data.coldStoreData.coordinates.split(',')[1] : "", [Validators.required, Validators.min(-180), Validators.max(180)]]
    });
  }

  getCustomers() {
    this.isLoading = true;
    this._devicesService.getCustomers(this.environment)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.customers = res.filter(item => !item.name.includes("compass") && !item.name.includes("nbmission")).map(item => ({
          code: item.id,
          description: item.name,
          displayDescription: item.name
        }));
        this.isLoading = false;
      }, error => {
        this.isLoading = false;
        this._toastr.error('Error loading customers', 'Error');
      });
  }

  getCommodities() {
    this._devicesService.getCommodities(this.environment)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.commodities = res.data ? res.data.map(item => ({
          code: item.id,
          description: item.name,
          displayDescription: item.name
        })) : res.map(item => ({
          code: item.id,
          description: item.name,
          displayDescription: item.name
        }));
      }, error => {
        console.error('Error fetching commodities:', error);
        this._toastr.error('Error loading commodities', 'Error');
      });
  }

  onCustomerChange(customerId: number) {
    if (!customerId) return;
    
    this.isLoading = true;
    
    // Clear dependent fields first
    this.userDetails = [];
    this.profiles = [];
    this.profileTypes = [];
    this.userSites = [];
    this.customerDetailsForm.get('userId').disable();
    this.customerDetailsForm.get('siteId').disable();
    this.customerDetailsForm.get('profileId').disable();
    this.customerDetailsForm.get('profileTypeId').disable();
    
    // Fetch user details for selected customer
    this._devicesService.getUserDetailsByCustomer(customerId, this.environment)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        console.log('User details API response:', res);
        this.userDetails = res.data.filter(item => item.roles && item.roles[0] && item.roles[0].name === 'user');        
        if (this.userDetails && this.userDetails.length > 0) {
          console.log('Setting first user:', this.userDetails[0]);
          this.customerDetailsForm.get('userId').enable();
          this.customerDetailsForm.get('userId').setValue(this.userDetails[0].id);
          this.onUserChange(this.userDetails[0].id);
        } else {
          console.log('No users found');
          this.customerDetailsForm.get('userId').disable();
          this.customerDetailsForm.get('siteId').disable();
        }
      }, error => {
        console.error('Error fetching user details:', error);
        this.customerDetailsForm.get('userId').disable();
        this.customerDetailsForm.get('siteId').disable();
      });

    // Fetch profiles for selected customer
    this._devicesService.getProfilesByCustomer(customerId,  this.environment)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.profiles = res.data.map(item => ({
          code: item.id,
          description: item.name,
          displayDescription: item.name
        }));
        if (this.profiles && this.profiles.length > 0) {
          this.customerDetailsForm.get('profileId').enable();
          this.customerDetailsForm.get('profileId').setValue(this.profiles[0].code);
        } else {
          this.customerDetailsForm.get('profileId').disable();
        }
      }, error => {
        console.error('Error fetching profiles:', error);
        this.customerDetailsForm.get('profileId').disable();
      });

    // Fetch profile types for selected customer
    this._devicesService.getProfileTypesByCustomer(customerId, this.environment)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.profileTypes = res.data.map(item => ({
          code: item.id,
          description: item.name,
          displayDescription: item.name
        }));
        if (this.profileTypes && this.profileTypes.length > 0) {
          this.customerDetailsForm.get('profileTypeId').enable();
          this.customerDetailsForm.get('profileTypeId').setValue(this.profileTypes[0].code);
        } else {
          this.customerDetailsForm.get('profileTypeId').disable();
        }
        this.isLoading = false;
      }, error => {
        console.error('Error fetching profile types:', error);
        this.customerDetailsForm.get('profileTypeId').disable();
        this.isLoading = false;
        this._toastr.error('Error loading customer details', 'Error');
      });
  }

  onUserChange(userId: number) {
    console.log('onUserChange called with userId:', userId);
    
    if (!this.userDetails || this.userDetails.length === 0) {
      console.log('No user details available');
      return;
    }
    
    const user = this.userDetails.find(u => u.id === userId);
    console.log('Found user:', user);
    
    // Always clear the current site selection when user changes
    this.customerDetailsForm.get('siteId').setValue(null);
    
    if (user && user.sites && user.sites.length > 0) {
      console.log('User has sites:', user.sites);
      this.userSites = [...user.sites];
      this.customerDetailsForm.get('siteId').enable();
      this.customerDetailsForm.get('siteId').setValue(this.userSites[0].id);
      this._cdr.detectChanges();
    } else if (user) {
      console.log('User found but has no sites');
      this.userSites = [];
      this.customerDetailsForm.get('siteId').disable();
      this.customerDetailsForm.get('siteId').setValue(null);
      this._cdr.detectChanges();
    } else {
      console.log('User not found');
      this.userSites = [];
      this.customerDetailsForm.get('siteId').disable();
      this.customerDetailsForm.get('siteId').setValue(null);
      this._cdr.detectChanges();
    }
  }

  onSubmit() {
    if (this.customerDetailsForm.invalid) {
      this._toastr.error('Please fill all required fields', 'Validation Error');
      return;
    }

    let formData = this.customerDetailsForm.getRawValue(); 
    let selectedUser = this.userDetails.find(user => user.id === formData.userId);
    formData.companyName = selectedUser ? selectedUser.name : '';
    formData.environment = this.environment;
    console.log('Submitting form data:', formData);
    
    this.dialogRef.close(formData);
  }

  onCancel() {
    this.dialogRef.close();
  }

  get f() { return this.customerDetailsForm.controls; }

  get isFormValid(): boolean {
    const formValue = this.customerDetailsForm.getRawValue();
    return this.customerDetailsForm.valid && 
           formValue.customerId && 
           formValue.userId && 
           formValue.siteId && 
           formValue.profileId && 
           formValue.profileTypeId && 
           formValue.commodityId && 
           formValue.name && 
           formValue.latitude && 
           formValue.longitude;
  }

  setupFormListeners() {
    // Listen to customerId changes
    this.customerDetailsForm.get('customerId').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(customerId => {
        if (customerId) {
          this.onCustomerChange(customerId);
        } else {
          // Clear all dependent fields when no customer is selected
          this.userDetails = [];
          this.profiles = [];
          this.profileTypes = [];
          this.userSites = [];
          this.customerDetailsForm.get('userId').disable();
          this.customerDetailsForm.get('siteId').disable();
          this.customerDetailsForm.get('profileId').disable();
          this.customerDetailsForm.get('profileTypeId').disable();
        }
      });

    // Listen to userId changes
    this.customerDetailsForm.get('userId').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(userId => {
        if (userId) {
          this.onUserChange(userId);
        } else {
          // Clear sites when no user is selected
          this.userSites = [];
          this.customerDetailsForm.get('siteId').disable();
          this.customerDetailsForm.get('siteId').setValue(null);
        }
      });
  }

  getCurrentLocation() {
    this.isGettingLocation = true;
    this.locationError = '';

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          this.customerDetailsForm.patchValue({
            latitude: this.coordinates.latitude,
            longitude: this.coordinates.longitude
          });
          this.isGettingLocation = false;
          this._toastr.success('Location obtained successfully', 'Success');
        },
        (error) => {
          this.isGettingLocation = false;
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              this.locationError = 'Location access denied. Default location (Mohali) coordinates used.';
              break;
            case error.POSITION_UNAVAILABLE:
              this.locationError = 'Location information unavailable. Default location (Mohali) coordinates used.';
              break;
            case error.TIMEOUT:
              this.locationError = 'Location request timed out. Default location (Mohali) coordinates used.';
              break;
            default:
              this.locationError = 'Unable to get location. Default location  (Mohali) coordinates used.';
              break;
          }
          this.customerDetailsForm.patchValue({
            latitude: "30.6962487",
            longitude: "76.6961193"
          });
          this._toastr.warning(this.locationError, 'Location Error');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    } else {
      this.isGettingLocation = false;
      this.locationError = 'Geolocation is not supported by this browser. Please enter coordinates manually.';
      this._toastr.warning(this.locationError, 'Location Error');
    }
  }

  onCoordinatesChange() {
    const lat = this.customerDetailsForm.get('latitude');
    const lng = this.customerDetailsForm.get('longitude');
    
    if (lat && lng && lat.value !== null && lng.value !== null) {
      const latitude = parseFloat(lat.value);
      const longitude = parseFloat(lng.value);
      
      if (!isNaN(latitude) && !isNaN(longitude)) {
        this.coordinates = { latitude, longitude };
      }
    }
  }

  selectEnvironment(environment: string): void {
    this.selectedEnvironment = environment;
    this.environment = environment;
  }

  proceedWithEnvironment(): void {
    if (!this.selectedEnvironment) {
      this._toastr.error('Please select an environment', 'Validation Error');
      return;
    }
    
    this.environmentSelectionMode = false;
    this.loadCustomerData();
  }

  backToEnvironmentSelection(): void {
    this.environmentSelectionMode = true;
    this.resetFormData();
  }

  private loadCustomerData(): void {
    this.getCustomers();
    this.getCommodities();
    this.getCurrentLocation();
    this.setupFormListeners();
  }

  private resetFormData(): void {
    this.customers = [];
    this.userDetails = [];
    this.profiles = [];
    this.profileTypes = [];
    this.userSites = [];
    this.commodities = [];
    this.customerDetailsForm.reset();
    this._createForm();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
} 